import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Candidate } from '@/types.ts';
import {
  CANDIDATES_QUERY_KEY,
  fetchCandidateDetail,
  findCandidateInPipeline,
} from '@/features/candidates/candidateData';

export const useCandidateProfile = (profileId?: string) => {
  const queryClient = useQueryClient();

  const getCachedCandidate = useCallback(() => {
    if (!profileId) return null;

    const cachedLists = queryClient.getQueriesData<Candidate[]>({
      queryKey: CANDIDATES_QUERY_KEY,
    });

    for (const [, list] of cachedLists) {
      const matched = list?.find(
        (item) => item.id === profileId || item.applicationId === profileId,
      );

      if (matched) {
        return matched;
      }
    }

    return null;
  }, [profileId, queryClient]);

  return useQuery<Candidate | null>({
    queryKey: ['candidate-profile', profileId],
    enabled: Boolean(profileId),
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: () => getCachedCandidate(),
    queryFn: async () => {
      if (!profileId) return null;

      const fallbackCandidate = getCachedCandidate() || (await findCandidateInPipeline(profileId));
      const applicationId = fallbackCandidate?.applicationId || profileId;

      try {
        return await fetchCandidateDetail(applicationId);
      } catch {
        return fallbackCandidate;
      }
    },
  });
};
